import { ref } from 'vue';
import statementResource from '@/apps/payments/payments/statement/resources/statement';
import settingsResource from '@/apps/payments/resources/settings';
import { useErrorHandler } from '@/commons/composables/useErrorHandler';
import { APPS } from '@/commons/constants/apps';
import { authAs, getCurrentApp, isAppAllowed } from '@/commons/services/apps';

const balanceValue = ref(0);
const waitingValue = ref(0);
const validationAccount = ref(false);
const isLoadingBalance = ref(false);
const balanceError = ref(null);

let initializeBalancePromise = null;
let refreshBalancePromise = null;
let isInitialized = false;

const getWaitingValue = balance => {
  return balance.waitingValue ?? balance.waiting_value ?? 0;
};

const updateBalanceValues = async () => {
  const balance = await statementResource.getBalance();

  balanceValue.value = balance.value ?? 0;
  waitingValue.value = getWaitingValue(balance);
};

export const usePaymentsBalance = () => {
  const { handleError } = useErrorHandler();

  const refreshBalance = async () => {
    if (!isAppAllowed(APPS.PAYMENTS.key)) {
      return;
    }

    if (getCurrentApp()?.name !== APPS.PAYMENTS.key) {
      return;
    }

    if (initializeBalancePromise) {
      return initializeBalancePromise;
    }

    if (refreshBalancePromise) {
      return refreshBalancePromise;
    }

    refreshBalancePromise = (async () => {
      isLoadingBalance.value = true;
      balanceError.value = null;

      try {
        await updateBalanceValues();
      } catch (error) {
        balanceError.value = error;
        handleError(error, 'Erro ao carregar o saldo');
      } finally {
        isLoadingBalance.value = false;
        refreshBalancePromise = null;
      }
    })();

    return refreshBalancePromise;
  };

  const initializeBalance = async () => {
    if (!isAppAllowed(APPS.PAYMENTS.key)) {
      return;
    }

    if (isInitialized) {
      return;
    }

    if (initializeBalancePromise) {
      return initializeBalancePromise;
    }

    initializeBalancePromise = (async () => {
      isLoadingBalance.value = true;
      balanceError.value = null;

      try {
        await authAs(APPS.PAYMENTS.key);

        const settings = await settingsResource.get();

        validationAccount.value = settings.validationAccount ?? false;

        await updateBalanceValues();

        isInitialized = true;
      } catch (error) {
        balanceError.value = error;
        handleError(error, 'Erro ao inicializar o saldo');
      } finally {
        isLoadingBalance.value = false;
        initializeBalancePromise = null;
      }
    })();

    return initializeBalancePromise;
  };

  return {
    balanceValue,
    waitingValue,
    validationAccount,
    isLoadingBalance,
    balanceError,
    initializeBalance,
    refreshBalance
  };
};
